import { html, nothing } from 'lit';

export const VERSION_INFO_SECTION_RE = /^=== .+ ===$/;

const LANG_LABELS = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  ar: 'العربية',
  zh: '中文',
  de: 'Deutsch',
  it: 'Italiano',
  ru: 'Русский',
  ja: '日本語',
  ko: '한국어',
};

/** Espacio extra antes de DEPLOYMENT INFO (tras el bloque de plataforma). */
export function normalizeVersionInfoSpacing(text) {
  if (!text) return '';
  return text.replace(
    /(Backend WAR build date:[^\n]*)\n(=== DEPLOYMENT INFO ===)/,
    '$1\n\n$2',
  );
}

export function parseAvailableLanguages(raw) {
  if (!raw) return [];
  return String(raw)
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

function renderLanguageChips(codes, currentLang, label = 'availableLanguages:') {
  return html`
    <div class="version-info-lang-row">
      <span class="version-info-field-label">${label}</span>
      <div class="version-info-lang-chips">
        ${codes.map((code) => html`
          <span
            class="version-info-lang-chip ${code === currentLang ? 'current' : ''}"
            title="${LANG_LABELS[code] || code}"
          >
            <span class="code">${code.toUpperCase()}</span>
            <span class="name">${LANG_LABELS[code] || code}</span>
          </span>
        `)}
      </div>
    </div>
  `;
}

function renderLine(line, options = {}) {
  const trimmed = line.trim();
  if (VERSION_INFO_SECTION_RE.test(trimmed)) {
    return html`<div class="version-info-section-title">${trimmed}</div>`;
  }
  if (line === '') {
    return html`<div class="version-info-spacer" aria-hidden="true"></div>`;
  }

  const colonIdx = line.indexOf(': ');
  if (colonIdx > 0) {
    const label = line.slice(0, colonIdx + 1);
    const value = line.slice(colonIdx + 2);
    if (label === 'availableLanguages:') {
      const codes = parseAvailableLanguages(value);
      if (codes.length) {
        return renderLanguageChips(codes, options.currentLang);
      }
    }
    if (label === 'bot_ai:') {
      return html`
        <div class="version-info-line version-info-bot-ai">
          <span class="version-info-field-label">${label}</span>
          <span class="version-info-bot-ai-value">${value}</span>
        </div>
      `;
    }
    return html`<div class="version-info-line"><span class="version-info-field-label">${label}</span> ${value}</div>`;
  }
  return html`<div class="version-info-line">${line}</div>`;
}

/**
 * @param {string} text
 * @param {{ config?: object, currentLang?: string }} [options]
 */
export function renderVersionInfoBody(text, options = {}) {
  const normalized = normalizeVersionInfoSpacing(text);
  const configLangs = parseAvailableLanguages(options.config?.availableLanguages);
  let injectedConfigLangs = false;

  const lines = normalized.split('\n').map((line) => {
    const trimmed = line.trim();
    if (
      !injectedConfigLangs
      && configLangs.length
      && trimmed === '=== RUNTIME CONFIG (support) ==='
    ) {
      injectedConfigLangs = true;
      return [
        renderLine(line, options),
        renderLanguageChips(configLangs, options.currentLang),
      ];
    }
    const rendered = renderLine(line, options);
    if (
      line.startsWith('availableLanguages:')
      && configLangs.length
      && injectedConfigLangs
    ) {
      return null;
    }
    return rendered;
  });

  const flat = lines.flat().filter((item) => item !== null);

  return html`
    <div class="version-info-formatted">
      ${flat}
    </div>
  `;
}

/**
 * Aviso UI cuando deploy-diff-advisory.json reporta drift (trazit-tracking #343).
 * @param {object|null} advisory
 * @param {string} [lang]
 */
export function renderDeployDiffAdvisoryBanner(advisory, lang = 'en') {
  if (!advisory || advisory.status !== 'advisory' || !advisory.mismatchCount) return null;

  const title = lang === 'es'
    ? 'Drift de componentes (advisory post-deploy)'
    : 'Component version drift (post-deploy advisory)';
  const hint = lang === 'es'
    ? 'Estas versiones desplegadas no coinciden con package_de_referencia. No bloquea la app; revisar antes del proximo bump.'
    : 'Deployed versions differ from package_de_referencia. Non-blocking; review before next bump.';
  const colRef = lang === 'es' ? 'Referencia' : 'Reference';
  const colDeployed = lang === 'es' ? 'Desplegado' : 'Deployed';

  return html`
    <div class="version-info-deploy-advisory" role="status">
      <div class="version-info-deploy-advisory-title">⚠ ${title}</div>
      <p class="version-info-deploy-advisory-hint">${hint}</p>
      <ul class="version-info-deploy-advisory-list">
        ${(advisory.mismatches || []).slice(0, 12).map((m) => html`
          <li>
            <code>${m.pkg}</code>:
            ${colRef} <strong>${m.ref}</strong> → ${colDeployed} <strong>${m.deployed}</strong>
          </li>
        `)}
      </ul>
      ${advisory.mismatchCount > 12 ? html`
        <p class="version-info-deploy-advisory-more">+${advisory.mismatchCount - 12} más…</p>
      ` : nothing}
    </div>
  `;
}
